import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { Modal } from '../../components/UI/Modal';
import { useToast } from '../../components/UI/ToastProvider';
import { supportGroupService } from '../../services/supportGroupService';
import { inboxService } from '../../services/inboxService';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { api } from '../../services/api';
import {
  CHANNEL_TYPE_LABELS,
  ChannelType,
} from '../../types/atendimentoEnums';
import { Inbox } from '../../types/inbox';
import {
  CreateSupportGroupRoutingRuleRequest,
  SupportGroup,
  SupportGroupMemberInput,
  SupportGroupMemberType,
  SupportGroupRoutingRule,
  SupportGroupStats,
} from '../../types/supportGroup';

interface RoleOption {
  id: string;
  name: string;
}

interface TeamOption {
  id: string;
  name: string;
}

interface UserOption {
  id: string;
  name: string;
}

const emptyGroupForm = {
  name: '',
  description: '',
  color: '#2563eb',
  isActive: true,
};

const emptyRuleForm: CreateSupportGroupRoutingRuleRequest = {
  name: '',
  priority: 100,
  isActive: true,
  channelType: undefined,
  inboxId: undefined,
  keywordContains: '',
};

export const SupportGroupsPage: React.FC = () => {
  const { addToast } = useToast();
  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [stats, setStats] = useState<SupportGroupStats[]>([]);
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SupportGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<SupportGroup | null>(null);
  const [groupForm, setGroupForm] = useState(emptyGroupForm);
  const [memberInputs, setMemberInputs] = useState<SupportGroupMemberInput[]>([]);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [ruleForm, setRuleForm] = useState(emptyRuleForm);
  const [editingRule, setEditingRule] = useState<SupportGroupRoutingRule | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try
    {
      const [groupsData, statsData, inboxesData, usersResult, teamsData, rolesResponse] = await Promise.all([
        supportGroupService.getAll(),
        supportGroupService.getStats(),
        inboxService.getAll(),
        userService.getUsers('', 1, 200),
        teamService.getTeams(),
        api.get<RoleOption[]>('/roles'),
      ]);
      setGroups(groupsData);
      setStats(statsData);
      setInboxes(inboxesData);
      setUsers(usersResult.users.map(u => ({ id: u.id, name: u.name })));
      setTeams(teamsData.map(t => ({ id: t.id, name: t.name })));
      setRoles(rolesResponse.data);
    }
    catch
    {
      addToast('Erro ao carregar grupos de atendimento', 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const getStatsForGroup = (groupId: string) =>
    stats.find(s => s.supportGroupId === groupId);

  const filtered = groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));

  const openCreateModal = () => {
    setEditingGroup(null);
    setGroupForm(emptyGroupForm);
    setMemberInputs([]);
    setIsGroupModalOpen(true);
  };

  const openEditModal = (group: SupportGroup) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description ?? '',
      color: group.color ?? '#2563eb',
      isActive: group.isActive,
    });
    setMemberInputs(group.members.map(m => ({
      memberType: m.memberType,
      userId: m.userId,
      teamId: m.teamId,
      roleId: m.roleId,
    })));
    setIsGroupModalOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim())
    {
      addToast('Informe o nome do grupo', 'error');
      return;
    }

    try
    {
      if (editingGroup)
      {
        await supportGroupService.update(editingGroup.id, {
          name: groupForm.name.trim(),
          description: groupForm.description || null,
          color: groupForm.color || null,
          isActive: groupForm.isActive,
        });
        await supportGroupService.updateMembers(editingGroup.id, { members: memberInputs });
        addToast('Grupo atualizado', 'success');
      }
      else
      {
        await supportGroupService.create({
          name: groupForm.name.trim(),
          description: groupForm.description || null,
          color: groupForm.color || null,
          isActive: groupForm.isActive,
          members: memberInputs,
        });
        addToast('Grupo criado', 'success');
      }

      setIsGroupModalOpen(false);
      await loadData();
    }
    catch
    {
      addToast('Erro ao salvar grupo', 'error');
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete)
    {
      return;
    }

    try
    {
      await supportGroupService.delete(groupToDelete.id);
      addToast('Grupo excluído', 'success');
      setIsDeleteModalOpen(false);
      setGroupToDelete(null);
      await loadData();
    }
    catch
    {
      addToast('Erro ao excluir grupo', 'error');
    }
  };

  const addMemberInput = () => {
    setMemberInputs(prev => [...prev, { memberType: SupportGroupMemberType.User, userId: '' }]);
  };

  const updateMemberInput = (index: number, input: SupportGroupMemberInput) => {
    setMemberInputs(prev => prev.map((item, i) => (i === index ? input : item)));
  };

  const removeMemberInput = (index: number) => {
    setMemberInputs(prev => prev.filter((_, i) => i !== index));
  };

  const resetRuleForm = () => {
    setRuleForm(emptyRuleForm);
    setEditingRule(null);
  };

  const handleSaveRule = async (groupId: string) => {
    if (!ruleForm.name.trim())
    {
      addToast('Informe o nome da regra', 'error');
      return;
    }

    try
    {
      if (editingRule)
      {
        await supportGroupService.updateRoutingRule(editingRule.id, {
          ...ruleForm,
          name: ruleForm.name.trim(),
          keywordContains: ruleForm.keywordContains || null,
        });
        addToast('Regra atualizada', 'success');
      }
      else
      {
        await supportGroupService.createRoutingRule(groupId, {
          ...ruleForm,
          name: ruleForm.name.trim(),
          keywordContains: ruleForm.keywordContains || null,
        });
        addToast('Regra criada', 'success');
      }

      resetRuleForm();
      await loadData();
    }
    catch
    {
      addToast('Erro ao salvar regra', 'error');
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try
    {
      await supportGroupService.deleteRoutingRule(ruleId);
      addToast('Regra excluída', 'success');
      await loadData();
    }
    catch
    {
      addToast('Erro ao excluir regra', 'error');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Grupos de Atendimento</h1>
          <p className="text-gray-600 text-sm mt-1">Organize filas, membros e roteamento automático</p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>Novo grupo</Button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <input
          type="text"
          placeholder="Buscar grupos..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-4">Nenhum grupo configurado.</p>
          <Button variant="primary" onClick={openCreateModal}>Criar primeiro grupo</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(group => {
            const groupStats = getStatsForGroup(group.id);
            const isExpanded = expandedGroupId === group.id;

            return (
              <div key={group.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span
                      className="mt-1 h-3 w-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: group.color ?? '#2563eb' }}
                    />
                    <div>
                      <h2 className="font-semibold text-gray-900">{group.name}</h2>
                      {group.description && (
                        <p className="text-sm text-gray-600">{group.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${group.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {group.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-800">
                          {group.members.length} membro(s)
                        </span>
                        <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-800">
                          {group.routingRules.length} regra(s)
                        </span>
                        {groupStats && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">
                            {groupStats.openConversationCount} aberta(s) / {groupStats.totalConversationCount} total
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setExpandedGroupId(isExpanded ? null : group.id)}>
                      {isExpanded ? 'Ocultar regras' : 'Regras'}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openEditModal(group)}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        setGroupToDelete(group);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t p-4 bg-gray-50 space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Regras de roteamento</h3>
                      {group.routingRules.length === 0 ? (
                        <p className="text-sm text-gray-500">Nenhuma regra configurada.</p>
                      ) : (
                        <ul className="space-y-2">
                          {group.routingRules.map(rule => (
                            <li key={rule.id} className="bg-white rounded-md border p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                              <div className="text-sm">
                                <p className="font-medium">{rule.name}</p>
                                <p className="text-gray-500">
                                  Prioridade {rule.priority}
                                  {rule.channelType != null ? ` · ${CHANNEL_TYPE_LABELS[rule.channelType as ChannelType]}` : ''}
                                  {rule.inboxId ? ` · Caixa específica` : ''}
                                  {rule.keywordContains ? ` · Palavra: "${rule.keywordContains}"` : ''}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setEditingRule(rule);
                                    setRuleForm({
                                      name: rule.name,
                                      priority: rule.priority,
                                      isActive: rule.isActive,
                                      channelType: rule.channelType ?? undefined,
                                      inboxId: rule.inboxId ?? undefined,
                                      keywordContains: rule.keywordContains ?? '',
                                    });
                                  }}
                                >
                                  Editar
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => void handleDeleteRule(rule.id)}>
                                  Excluir
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="bg-white rounded-md border p-4 space-y-3">
                      <h4 className="text-sm font-semibold">{editingRule ? 'Editar regra' : 'Nova regra'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Nome da regra"
                          value={ruleForm.name}
                          onChange={e => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                        <input
                          type="number"
                          placeholder="Prioridade"
                          value={ruleForm.priority}
                          onChange={e => setRuleForm(prev => ({ ...prev, priority: Number(e.target.value) }))}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                        <select
                          value={ruleForm.channelType ?? ''}
                          onChange={e => setRuleForm(prev => ({
                            ...prev,
                            channelType: e.target.value === '' ? undefined : e.target.value as ChannelType,
                          }))}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">Qualquer canal</option>
                          {Object.entries(CHANNEL_TYPE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <select
                          value={ruleForm.inboxId ?? ''}
                          onChange={e => setRuleForm(prev => ({
                            ...prev,
                            inboxId: e.target.value || undefined,
                          }))}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">Qualquer caixa</option>
                          {inboxes.map(inbox => (
                            <option key={inbox.id} value={inbox.id}>{inbox.name}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Palavra-chave no conteúdo"
                          value={ruleForm.keywordContains ?? ''}
                          onChange={e => setRuleForm(prev => ({ ...prev, keywordContains: e.target.value }))}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button variant="primary" size="sm" onClick={() => void handleSaveRule(group.id)}>
                          {editingRule ? 'Salvar regra' : 'Adicionar regra'}
                        </Button>
                        {editingRule && (
                          <Button variant="secondary" size="sm" onClick={resetRuleForm}>Cancelar edição</Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        title={editingGroup ? 'Editar grupo' : 'Novo grupo'}
      >
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Nome (ex.: Comercial, Suporte)"
            value={groupForm.name}
            onChange={e => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <textarea
            placeholder="Descrição"
            value={groupForm.description}
            onChange={e => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            rows={2}
          />
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={groupForm.color}
              onChange={e => setGroupForm(prev => ({ ...prev, color: e.target.value }))}
              className="h-10 w-14 border border-gray-300 rounded"
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={groupForm.isActive}
                onChange={e => setGroupForm(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              Grupo ativo
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold">Membros</h4>
              <Button variant="secondary" size="sm" onClick={addMemberInput}>Adicionar membro</Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {memberInputs.map((member, index) => (
                <div key={index} className="flex flex-col md:flex-row gap-2">
                  <select
                    value={member.memberType}
                    onChange={e => updateMemberInput(index, {
                      memberType: e.target.value as SupportGroupMemberType,
                      userId: undefined,
                      teamId: undefined,
                      roleId: undefined,
                    })}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value={SupportGroupMemberType.User}>Usuário</option>
                    <option value={SupportGroupMemberType.Team}>Equipe</option>
                    <option value={SupportGroupMemberType.Role}>Perfil</option>
                  </select>
                  {member.memberType === SupportGroupMemberType.User && (
                    <select
                      value={member.userId ?? ''}
                      onChange={e => updateMemberInput(index, { ...member, userId: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Selecione usuário</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.name}</option>
                      ))}
                    </select>
                  )}
                  {member.memberType === SupportGroupMemberType.Team && (
                    <select
                      value={member.teamId ?? ''}
                      onChange={e => updateMemberInput(index, { ...member, teamId: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Selecione equipe</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  )}
                  {member.memberType === SupportGroupMemberType.Role && (
                    <select
                      value={member.roleId ?? ''}
                      onChange={e => updateMemberInput(index, { ...member, roleId: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Selecione perfil</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  )}
                  <Button variant="danger" size="sm" onClick={() => removeMemberInput(index)}>Remover</Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsGroupModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={() => void handleSaveGroup()}>Salvar</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Excluir grupo"
      >
        <p className="text-sm text-gray-600 mb-4">
          Deseja excluir o grupo <strong>{groupToDelete?.name}</strong>? Conversas existentes perderão a referência ao grupo.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={() => void handleDeleteGroup()}>Excluir</Button>
        </div>
      </Modal>
    </div>
  );
};
